import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Scale, Mail, Lock, ChevronRight, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { BrandLogo } from "../components/Brand";

export default function LoginPage() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const { login } = useAuth();
    const navigate = useNavigate();
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        
        try {
            await login(email, password);
            toast.success("Welcome back!");
            navigate("/dashboard");
        } catch (err) {
            const message = err.response?.data?.detail || "Invalid email or password";
            setError(message);
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };
    
    return (
        <div className="min-h-screen bg-background flex flex-col">
            {/* Navigation */}
            <nav className="px-6 py-4">
                <div className="max-w-7xl mx-auto">
                    <BrandLogo />
                </div>
            </nav>
            
            {/* Login Form */}
            <div className="flex-1 flex items-center justify-center px-6 py-12">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="w-full max-w-md"
                >
                    <Card className="glass border-slate-800 rounded-2xl">
                        <CardHeader className="text-center pb-2">
                            <CardTitle className="font-heading text-2xl text-white">Welcome Back</CardTitle>
                            <CardDescription className="text-slate-400">
                                Sign in to continue your legal conquest
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="pt-6">
                            <form onSubmit={handleSubmit} className="space-y-6">
                                {error && (
                                    <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm">
                                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                        <span>{error}</span>
                                    </div>
                                )}
                                
                                <div className="space-y-2">
                                    <Label htmlFor="email" className="text-slate-300">Email</Label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                                        <Input
                                            id="email"
                                            type="email"
                                            placeholder="you@lawschool.edu"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            required
                                            data-testid="login-email-input"
                                            className="pl-10 bg-slate-950/50 border-slate-800 focus:border-primary h-12 rounded-xl"
                                        />
                                    </div>
                                </div>
                                
                                <div className="space-y-2">
                                    <Label htmlFor="password" className="text-slate-300">Password</Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                                        <Input
                                            id="password"
                                            type="password"
                                            placeholder="••••••••"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                            data-testid="login-password-input"
                                            className="pl-10 bg-slate-950/50 border-slate-800 focus:border-primary h-12 rounded-xl"
                                        />
                                    </div>
                                </div>
                                
                                <Button
                                    type="submit"
                                    disabled={loading}
                                    data-testid="login-submit-btn"
                                    className="w-full rounded-full bg-primary hover:bg-primary/90 h-12 text-base font-semibold"
                                >
                                    {loading ? (
                                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                        <>
                                            Sign In
                                            <ChevronRight className="w-4 h-4 ml-2" />
                                        </>
                                    )}
                                </Button>
                            </form>
                            
                            <div className="mt-6 text-center">
                                <p className="text-slate-400 text-sm">
                                    Don't have an account?{" "}
                                    <Link 
                                        to="/register" 
                                        className="text-primary hover:underline font-medium"
                                        data-testid="login-register-link"
                                    >
                                        Create one
                                    </Link>
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>
            </div>
        </div>
    );
}
